import React, { useState, useMemo } from 'react';
import {
  Flag,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  ArrowDownLeft,
  MessageSquare,
} from 'lucide-react';
import {
  SectionCard,
  MetricCard,
  TabLoading,
  TabError,
  TabEmpty,
  UserMiniCard,
  StatusBadge,
  getGenericStatusVariant,
} from './shared';
import { useUserReports } from '../../hooks/useUserTabs';
import { cn, formatDateTime, formatRelativeTime } from '../../utils/helpers';
import type { UserReportItem } from '../../types/userTabs';

// ============================================================================
// Types
// ============================================================================

type DirectionFilter = 'all' | 'made' | 'received';

interface ReportsTabProps {
  userId: number;
}

// ============================================================================
// Sub-components
// ============================================================================

const DirectionToggle: React.FC<{
  value: DirectionFilter;
  onChange: (v: DirectionFilter) => void;
  counts: { all: number; made: number; received: number };
}> = ({ value, onChange, counts }) => {
  const options: { key: DirectionFilter; label: string; icon: React.ElementType }[] = [
    { key: 'all', label: 'All', icon: Flag },
    { key: 'made', label: 'Made by User', icon: ArrowUpRight },
    { key: 'received', label: 'Against User', icon: ArrowDownLeft },
  ];

  return (
    <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/20 border border-border/50">
      {options.map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
            value === key
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          )}
        >
          <Icon className="h-3 w-3" />
          {label}
          <span
            className={cn(
              'ml-1 px-1.5 py-0.5 rounded-full text-[10px]',
              value === key
                ? 'bg-primary-foreground/20 text-primary-foreground'
                : 'bg-muted/30 text-muted-foreground'
            )}
          >
            {counts[key]}
          </span>
        </button>
      ))}
    </div>
  );
};

const ReportCard: React.FC<{
  report: UserReportItem;
  userId: number;
}> = ({ report, userId }) => {
  const isMadeByUser = report.reporterId === userId;

  return (
    <div className="rounded-lg border border-border/50 bg-muted/5 overflow-hidden">
      {/* Report Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/30 bg-muted/10">
        <div className="flex items-center gap-2.5">
          <div
            className={cn(
              'p-1.5 rounded-lg',
              isMadeByUser ? 'bg-amber-500/10' : 'bg-red-500/10'
            )}
          >
            {isMadeByUser ? (
              <ArrowUpRight className="h-3.5 w-3.5 text-amber-400" />
            ) : (
              <ArrowDownLeft className="h-3.5 w-3.5 text-red-400" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">
              {isMadeByUser ? 'Report Made' : 'Report Received'}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {formatRelativeTime(report.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge
            status={report.status.replace(/_/g, ' ')}
            variant={getGenericStatusVariant(report.status)}
          />
          <span className="text-[10px] text-muted-foreground font-mono">#{report.id}</span>
        </div>
      </div>

      {/* Report Body */}
      <div className="p-4 space-y-3">
        {/* Reporter / Reported */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1.5">
              Reporter
            </p>
            <UserMiniCard
              user={{
                id: report.reporter.id,
                name: report.reporter.name,
                email: report.reporter.email,
                profileImage: report.reporter.profileImage,
              }}
            />
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1.5">
              Reported
            </p>
            <UserMiniCard
              user={{
                id: report.reported.id,
                name: report.reported.name,
                email: report.reported.email,
                profileImage: report.reported.profileImage,
              }}
            />
          </div>
        </div>

        {/* Reason */}
        <div>
          <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1">Reason</p>
          <p className="text-sm text-foreground font-medium">{report.reason.replace(/_/g, ' ')}</p>
        </div>

        {/* Description */}
        {report.description && (
          <div>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1">
              Description
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">{report.description}</p>
          </div>
        )}

        {/* Chat Room Link */}
        {report.chatRoomId && (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <MessageSquare className="h-3 w-3" />
            <span>Chat Room #{report.chatRoomId}</span>
          </div>
        )}

        {/* Review Info */}
        {report.reviewedAt && (
          <div className="mt-3 pt-3 border-t border-border/30">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1">
              Review
            </p>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-foreground">
                  Reviewed {formatDateTime(report.reviewedAt)}
                  {report.reviewedById && (
                    <span className="text-muted-foreground"> by Admin #{report.reviewedById}</span>
                  )}
                </p>
                {report.reviewNote && (
                  <p className="text-xs text-muted-foreground mt-1">{report.reviewNote}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div className="flex items-center gap-4 text-[10px] text-muted-foreground pt-2">
          <span>Created: {formatDateTime(report.createdAt)}</span>
          {report.updatedAt !== report.createdAt && (
            <span>Updated: {formatDateTime(report.updatedAt)}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const ReportsTab: React.FC<ReportsTabProps> = ({ userId }) => {
  const { items, total, loading, error, refetch } = useUserReports(userId, true, 'all');
  const [direction, setDirection] = useState<DirectionFilter>('all');

  const typedItems = items as UserReportItem[];

  const filteredItems = useMemo(() => {
    if (!typedItems) return [];
    switch (direction) {
      case 'made':
        return typedItems.filter((r) => r.reporterId === userId);
      case 'received':
        return typedItems.filter((r) => r.reportedId === userId);
      default:
        return typedItems;
    }
  }, [typedItems, direction, userId]);

  const counts = useMemo(() => {
    if (!typedItems) return { all: 0, made: 0, received: 0 };
    return {
      all: typedItems.length,
      made: typedItems.filter((r) => r.reporterId === userId).length,
      received: typedItems.filter((r) => r.reportedId === userId).length,
    };
  }, [typedItems, userId]);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!typedItems || typedItems.length === 0) {
    return <TabEmpty message="No reports found for this user" icon={Flag} />;
  }

  const pendingCount = typedItems.filter(
    (r) => r.status === 'PENDING' || r.status === 'PENDING_REVIEW' || r.status === 'UNDER_REVIEW'
  ).length;
  const resolvedCount = typedItems.filter(
    (r) => r.status === 'RESOLVED' || r.status === 'COMPLETED' || r.status === 'DISMISSED'
  ).length;

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard
          label="Total Reports"
          value={total}
          icon={Flag}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <MetricCard
          label="Reports Made"
          value={counts.made}
          icon={ArrowUpRight}
          color="text-amber-400"
          bgColor="bg-amber-500/10"
        />
        <MetricCard
          label="Reports Received"
          value={counts.received}
          icon={ArrowDownLeft}
          color="text-red-400"
          bgColor="bg-red-500/10"
        />
        <MetricCard
          label="Pending"
          value={pendingCount}
          icon={Clock}
          color="text-orange-400"
          bgColor="bg-orange-500/10"
          subtitle={resolvedCount > 0 ? `${resolvedCount} resolved` : undefined}
        />
      </div>

      {/* Direction Filter */}
      <div className="flex items-center justify-between">
        <DirectionToggle value={direction} onChange={setDirection} counts={counts} />
      </div>

      {/* Reports List */}
      {filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Flag className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No {direction === 'made' ? 'reports made by' : direction === 'received' ? 'reports against' : 'reports for'} this user
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredItems.map((report) => (
            <ReportCard key={report.id} report={report} userId={userId} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportsTab;
